import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { Button } from '../components/shared/Button';
import './ScoreForm.css';

const ScoreForm: React.FC = () => {
  const navigate = useNavigate();
  const { teams, playlist } = useGame();
  const printRef = useRef<HTMLDivElement>(null);
  
  const [selectedRound, setSelectedRound] = useState(1);
  const [songsPerRound] = useState(10);
  const [includeAnswers, setIncludeAnswers] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  
  // Calculate available rounds based on playlist
  const totalSongs = playlist.length;
  const totalRounds = Math.ceil(totalSongs / songsPerRound);
  
  // Get songs for selected round
  const getRoundSongs = (roundNum: number) => {
    const startIndex = (roundNum - 1) * songsPerRound;
    const endIndex = startIndex + songsPerRound;
    return playlist.slice(startIndex, endIndex);
  };
  
  const roundSongs = getRoundSongs(selectedRound);
  
  // Print functionality
  const handlePrint = () => {
    window.print();
  };
  
  // Generate forms for all teams
  const handlePrintAllTeams = () => {
    setSelectedTeamId('all');
    setTimeout(() => {
      window.print();
    }, 100);
  };

  return (
    <div className="score-form-page">
      <div className="score-form-controls no-print">
        <h1>📝 Scoreformulieren Generator</h1>
        
        <div className="control-row">
          <div className="control-group">
            <label>Ronde:</label>
            <select 
              value={selectedRound} 
              onChange={(e) => setSelectedRound(Number(e.target.value))}
            >
              {Array.from({ length: totalRounds || 1 }, (_, i) => (
                <option key={i + 1} value={i + 1}>Ronde {i + 1}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label>Team:</label>
            <select 
              value={selectedTeamId} 
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              <option value="">-- Selecteer team --</option>
              <option value="all">Alle teams ({teams.length})</option>
              <option value="blank">Blanco (zonder teamnaam)</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group checkbox">
            <label>
              <input 
                type="checkbox" 
                checked={includeAnswers}
                onChange={(e) => setIncludeAnswers(e.target.checked)}
              />
              Toon antwoorden (voor spelleider)
            </label>
          </div>
        </div>
        
        <div className="action-buttons">
          <Button variant="secondary" onClick={() => navigate('/admin')}>
            ← Terug
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            🖨️ Print Formulier
          </Button>
          {teams.length > 0 && (
            <Button variant="primary" onClick={handlePrintAllTeams}>
              🖨️ Print Alle Teams
            </Button>
          )}
        </div>
        
        {playlist.length === 0 && (
          <div className="warning-message">
            ⚠️ Geen nummers in playlist. Voeg eerst nummers toe in Prepare.
          </div>
        )}
      </div>
      
      {/* Printable forms */}
      <div ref={printRef} className="print-area">
        {selectedTeamId === 'all' ? (
          // Print form for each team
          teams.map(team => (
            <div key={team.id} className="score-form-sheet">
              <ScoreFormSheet 
                roundNumber={selectedRound}
                teamName={team.name}
                songs={roundSongs}
                includeAnswers={includeAnswers}
              />
            </div>
          ))
        ) : (
          // Single form
          <div className="score-form-sheet">
            <ScoreFormSheet 
              roundNumber={selectedRound}
              teamName={selectedTeamId === 'blank' ? '' : teams.find(t => t.id === selectedTeamId)?.name || ''}
              songs={roundSongs}
              includeAnswers={includeAnswers}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Individual score form sheet component
interface ScoreFormSheetProps {
  roundNumber: number;
  teamName: string;
  songs: Array<{ id: string; title: string; artist: string; year: number }>;
  includeAnswers: boolean;
}

const ScoreFormSheet: React.FC<ScoreFormSheetProps> = ({ 
  roundNumber, 
  teamName, 
  songs, 
  includeAnswers 
}) => {
  const songCount = songs.length || 10;
  
  return (
    <div className="form-sheet">
      <header className="form-header">
        <div className="form-logo">🎵 HITSTER</div>
        <h1>Scoreformulier - Ronde {roundNumber}</h1>
      </header>
      
      <div className="team-field">
        <label>Team:</label>
        <div className="team-input">
          {teamName || <span className="placeholder">_______________________</span>}
        </div>
      </div>
      
      <table className="score-table">
        <thead>
          <tr>
            <th className="col-num">#</th>
            <th className="col-year">Jaar</th>
            <th className="col-artist">Artiest</th>
            <th className="col-title">Titel</th>
            <th className="col-points">Punten</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: songCount }, (_, i) => {
            const song = songs[i];
            return (
              <tr key={i}>
                <td className="col-num">{i + 1}</td>
                <td className="col-year">
                  {includeAnswers && song ? (
                    <span className="answer">{song.year}</span>
                  ) : (
                    <div className="write-area"></div>
                  )}
                </td>
                <td className="col-artist">
                  {includeAnswers && song ? (
                    <span className="answer">{song.artist}</span>
                  ) : (
                    <div className="write-area"></div>
                  )}
                </td>
                <td className="col-title">
                  {includeAnswers && song ? (
                    <span className="answer">{song.title}</span>
                  ) : (
                    <div className="write-area"></div>
                  )}
                </td>
                <td className="col-points">
                  <div className="points-box">__/3</div>
                </td>
              </tr>
            );
          })}
          <tr className="total-row">
            <td colSpan={4} className="total-label">Totaal Ronde {roundNumber}:</td>
            <td className="col-points">
              <div className="points-box total">__/{songCount * 3}</div>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div className="form-instruction">
        ⬇️ Vul je scoreformulier in onder deze lijn ⬇️
      </div>
      
      <div className="scoring-guide">
        <h4>Puntentelling per nummer (max 3 punten):</h4>
        <div className="scoring-items">
          <span>✓ Jaar (±2): <strong>1 pt</strong></span>
          <span>✓ Artiest: <strong>1 pt</strong></span>
          <span>✓ Titel: <strong>1 pt</strong></span>
        </div>
      </div>
    </div>
  );
};

export default ScoreForm;
